// Third-party
import React from 'react';
import throttle from 'lodash/throttle';
import isEqual from 'lodash/isEqual';

// Components
import WindowScroller from '../../components/misc/WindowScroller';

// Utils
import { COMPARISON_CARD_WIDTH, UNIT_BASE } from '../../../utils/constants';
import { getNumDistributionScenarios } from '../../../utils/income-stream';
import ComparisonItem from '@/types/comparison-item';
import IncomeStreamScenario from '@/types/income-stream-scenario';

type Props = {
  items: ComparisonItem[];
  markupMode?: boolean;
  scrollContainerRef?: HTMLElement;
  editedComparisonItem?: ComparisonItem;
  editedIncomeStreamScenario?: IncomeStreamScenario;
  viewMarkup?: boolean;
  tablePaddingRef?: HTMLElement;
  previewMarkup?: boolean;
  isEditPinned: boolean;
};

type State = {
  disabled: boolean;
  cards: {
    width?: number;
    marginRight?: number;
  }[];
};

class ComparisonScroller extends React.Component<Props, State> {
  private comparisonScroller = React.createRef<WindowScroller>();

  readonly state: State = {
    disabled: true,
    cards: [],
  };

  hideComparisonScroller = () => {
    const { scrollContainerRef } = this.props;

    if (!scrollContainerRef) {
      return;
    }

    const { scrollWidth, offsetWidth } = scrollContainerRef;

    this.setState({
      disabled: scrollWidth <= offsetWidth,
    });
  };

  handleResize = throttle(() => {
    this.calculateCardWidths();
    this.hideComparisonScroller();
  }, 20);

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);

    this.calculateCardWidths();
    this.hideComparisonScroller();
  }

  componentDidUpdate(prevProps, prevState) {
    const { markupMode, items, editedComparisonItem, editedIncomeStreamScenario, viewMarkup, previewMarkup, scrollContainerRef, isEditPinned } = this.props;
    const { disabled } = this.state;
    const prevIsExpanded = prevProps.items.map(item => item.isExpanded);
    const currentIsExpanded = items.map(item => item.isExpanded);

    const itemsWithScenarios = items.filter(i => !!i.scenarios);

    if (itemsWithScenarios.length) {
      const totalScenarios = getNumDistributionScenarios(itemsWithScenarios);

      const prevItemsWithScenarios = prevProps.items.filter(i => !!i.scenarios);
      const prevTotalScenarios = getNumDistributionScenarios(prevItemsWithScenarios);

      if (prevTotalScenarios !== totalScenarios) {
        setTimeout(() => {
          this.calculateCardWidths();
          this.hideComparisonScroller();
        }, 100);
      }
    }

    if (
      prevProps.markupMode !== markupMode ||
      prevProps.items.length !== items.length ||
      prevState.disabled !== disabled ||
      prevProps.viewMarkup !== viewMarkup ||
      prevProps.previewMarkup !== previewMarkup ||
      prevProps.scrollContainerRef !== scrollContainerRef ||
      prevProps.isEditPinned !== isEditPinned ||
      !isEqual(currentIsExpanded, prevIsExpanded)
    ) {
      this.calculateCardWidths();
      this.hideComparisonScroller();
    }

    if (prevProps.editedComparisonItem !== editedComparisonItem || prevProps.editedIncomeStreamScenario !== editedIncomeStreamScenario) {
      setTimeout(() => {
        this.calculateCardWidths();
        this.hideComparisonScroller();
      }, 1000);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  calculateLastCardMargin = () => {
    const { scrollContainerRef, tablePaddingRef } = this.props;

    const comparisonScrollerRef = this.getComparisonScrollerRef();

    if (!scrollContainerRef || !comparisonScrollerRef || !tablePaddingRef) {
      return 0;
    }

    const fraction = tablePaddingRef.offsetWidth / scrollContainerRef.scrollWidth;

    const relativePadding = comparisonScrollerRef.offsetWidth * fraction;

    return 2 + relativePadding;
  };

  getComparisonScrollerRef = () => (this.comparisonScroller && this.comparisonScroller.current ? this.comparisonScroller.current.getWindowScrollerRef() : null);

  calculateCardWidths = () => {
    const { items } = this.props;

    const defaultCardWidth = UNIT_BASE * 3;

    const cards = items.map((item: ComparisonItem, i: number) => {
      const card = document.getElementById(`card-${item._id}`);
      const commentSection = document.getElementById(`comment-section-${item._id}`);

      if (!card) {
        return {};
      }

      let width = card.offsetWidth;
      let marginRight = 0;

      if (commentSection && !commentSection.classList.contains('hidden')) {
        // Account for additional margin on comment sections
        width += commentSection.offsetWidth + UNIT_BASE * 4;
      }

      if (i === items.length - 1) {
        marginRight = this.calculateLastCardMargin();
      }

      return {
        width: (width / COMPARISON_CARD_WIDTH) * defaultCardWidth,
        marginRight,
      };
    });

    this.setState({ cards });
  };

  renderScrollerCards = () => {
    const { cards } = this.state;

    return cards.map((card, i) => {
      const { width, marginRight } = card;

      return <div key={i} style={{ width, marginRight }} className="comparison-scroller-card" />;
    });
  };

  render() {
    const { scrollContainerRef } = this.props;
    const { disabled } = this.state;

    if (disabled) {
      return null;
    }

    return (
      <WindowScroller className="comparison-scroller" ref={this.comparisonScroller} scrollContainerRef={scrollContainerRef}>
        <div className="comparison-scroller-cards">{this.renderScrollerCards()}</div>
      </WindowScroller>
    );
  }
}

export default ComparisonScroller;
